import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/App";
import { X } from "lucide-react";

// Email + password auth modal. Opens when any CTA fires window "open-auth".
// Pass detail: { mode: "login" | "register" } to preselect the tab.
export default function AuthModal() {
  const nav = useNavigate();
  const { setUser } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setMode(e?.detail?.mode === "login" ? "login" : "register");
      setErr("");
      setOpen(true);
    };
    window.addEventListener("open-auth", handler);
    return () => window.removeEventListener("open-auth", handler);
  }, []);

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register" ? { email, password, name } : { email, password };
      const r = await http.post(path, body);
      setUser && setUser(r.data.user);
      setOpen(false);
      nav("/dashboard", { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong, please try again.");
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    "w-full rounded-lg bg-zinc-800 border border-zinc-700 px-3 py-2.5 text-sm text-white placeholder-zinc-500 outline-none focus:border-lime-400";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
      onClick={() => setOpen(false)}
      data-testid="auth-modal"
    >
      <div
        className="relative w-full max-w-sm rounded-2xl bg-zinc-900 border border-zinc-800 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute right-4 top-4 text-zinc-500 hover:text-white"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-2">
          <span className="inline-block w-7 h-7 rounded-lg bg-lime" />
          <span className="text-base font-black tracking-tight text-white">TagTitan</span>
        </div>
        <h2 className="mt-4 text-xl font-black text-white">
          {mode === "register" ? "Create your account" : "Welcome back"}
        </h2>
        <p className="mt-1 text-sm text-zinc-400">
          {mode === "register" ? "5 free generations, no card needed." : "Sign in to keep generating."}
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          {mode === "register" && (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              className={inputCls}
              data-testid="auth-name"
            />
          )}
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className={inputCls}
            data-testid="auth-email"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className={inputCls}
            data-testid="auth-password"
          />
          {err && <p className="text-sm text-red-400">{err}</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="auth-submit">
            {busy ? "Please wait..." : mode === "register" ? "Create account" : "Sign in"}
          </button>
        </form>

        <button
          onClick={() => {
            setMode(mode === "register" ? "login" : "register");
            setErr("");
          }}
          className="mt-4 w-full text-center text-sm text-zinc-400 hover:text-white"
          data-testid="auth-toggle"
        >
          {mode === "register" ? "Already have an account? Sign in" : "New here? Create an account"}
        </button>
      </div>
    </div>
  );
}
